"use client";
import { memo } from "react";
import { Spinner } from "@workspace/ui/components/spinner";
import { useResponsiveArtistLimit } from "@/hooks/shared/use-responsive-limit";
import type { SimpleArtist } from "../cards/artist-discovery-card";
import { ArtistDiscoveryCard } from "../cards/artist-discovery-card";
import { ArtistDiscoverySkeleton } from "../skeletons";
import { SearchEmptyState } from "../states";

interface ArtistGridProps {
	artists: SimpleArtist[];
	isLoading?: boolean;
	isInitialLoad?: boolean;
	searchTerm?: string;
	selectedGenre?: string;
	onClearSearch?: () => void;
	onClearGenre?: () => void;
	isLoadingMore?: boolean;
}

export const ArtistGrid = memo(function ArtistGrid({
	artists,
	isLoading = false,
	isInitialLoad = false,
	searchTerm = "",
	selectedGenre,
	onClearSearch,
	onClearGenre,
	isLoadingMore = false,
}: ArtistGridProps) {
	const { initialLimit, priorityCount } = useResponsiveArtistLimit();
	const showSkeletons = isInitialLoad || isLoading;
	const hasFilters =
		searchTerm || (selectedGenre && selectedGenre !== "all");
	const showEmptyState = !showSkeletons && artists.length === 0 && hasFilters;

	return (
		<div>
			<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
				{showSkeletons ? (
					// Loading skeletons
					Array.from({ length: initialLimit }, (_, i) => (
						<ArtistDiscoverySkeleton key={`artist-skeleton-${i}`} />
					))
				) : showEmptyState ? (
					<div className="col-span-full">
						{onClearSearch || onClearGenre ? (
							<SearchEmptyState
								searchTerm={searchTerm}
								selectedGenre={selectedGenre}
								onClearSearch={onClearSearch}
								onClearGenre={onClearGenre}
							/>
						) : (
							<div className="p-8 text-center text-muted-foreground">
								No artists found matching "{searchTerm}"
							</div>
						)}
					</div>
				) : (
					artists.map((artist, index) => {
						const isHighPriority = index < priorityCount;
						return (
							<ArtistDiscoveryCard
								key={artist.id}
								artist={artist}
								priority={isHighPriority}
								fetchPriority={isHighPriority ? "high" : "low"}
								loading={isHighPriority ? "eager" : "lazy"}
							/>
						);
					})
				)}
			</div>

			{/* Infinite scroll loading indicator */}
			{isLoadingMore && (
				<div className="mt-8 flex justify-center">
					<Spinner variant="muted" />
				</div>
			)}
		</div>
	);
});
